import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { useAuth } from "@/context/AuthContext";
import { api, apiError } from "@/lib/api";
import { normalizeWaveLink } from "@/lib/payments";
import { Button } from "@/components/ui/button";
import {
  ArrowLeft, Crown, Check, Users, Image as ImageIcon, Infinity as InfinityIcon,
  Waves, Smartphone, Copy, Loader2, Clock, ShieldCheck, ExternalLink,
} from "lucide-react";

export default function ProPlan() {
  const navigate = useNavigate();
  const { tailleur, updateTailleur } = useAuth();
  const [info, setInfo] = useState(null);
  const [status, setStatus] = useState(null);
  const [loading, setLoading] = useState(true);
  const [method, setMethod] = useState("wave");
  const [submitting, setSubmitting] = useState(false);

  const load = async () => {
    try {
      const [p, s] = await Promise.all([api.get("/platform/payment"), api.get("/subscription/status")]);
      setInfo(p.data);
      setStatus(s.data);
      if (s.data.plan) updateTailleur({ plan: s.data.plan, pro_expires_at: s.data.pro_expires_at });
    } catch (e) {
      /* ignore */
    } finally {
      setLoading(false);
    }
  };
  useEffect(() => {
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const price = info?.pro_price || 2500;
  const isPro = status?.plan === "pro";
  const pending = status?.pending;

  const copy = async (text, label) => {
    try { await navigator.clipboard.writeText(text); toast.success(`${label} copié`); }
    catch { toast.error("Copie impossible"); }
  };

  const payNow = () => {
    if (method === "wave") {
      if (info?.wave_link) { window.open(normalizeWaveLink(info.wave_link), "_blank"); return; }
      if (info?.wave_number) { copy(info.wave_number, "Numéro Wave"); toast.info("Ouvrez Wave et envoyez au numéro copié."); return; }
    }
    if (method === "orange" && info?.om_number) {
      copy(info.om_number, "Numéro Orange Money");
      toast.info("Ouvrez Orange Money (#144#) et envoyez au numéro copié.");
    }
  };

  const declarePaid = async () => {
    setSubmitting(true);
    try {
      await api.post("/subscription/request", { method });
      toast.success("Demande envoyée ! Activation après vérification.");
      await load();
    } catch (e) { toast.error(apiError(e)); } finally { setSubmitting(false); }
  };

  const formatDate = (iso) => { try { return new Date(iso).toLocaleDateString("fr-FR", { day: "2-digit", month: "long", year: "numeric" }); } catch { return ""; } };

  return (
    <div className="min-h-screen bg-tp-green text-white flex flex-col">
      <div className="px-4 pt-5">
        <button data-testid="pro-back-btn" onClick={() => navigate(-1)} className="w-10 h-10 rounded-full bg-white/15 flex items-center justify-center"><ArrowLeft size={20} /></button>
      </div>
      <div className="px-6 pt-6 text-center">
        <div className="w-20 h-20 rounded-3xl bg-tp-gold flex items-center justify-center mx-auto mb-5"><Crown size={40} className="text-tp-ink" /></div>
        <h1 className="font-head text-3xl font-extrabold">Plan Pro</h1>
        <p className="text-white/80 mt-2 text-base">Clients et photos illimités pour faire grandir votre atelier.</p>
      </div>

      {loading ? (
        <div className="flex justify-center py-16"><Loader2 className="animate-spin text-white" /></div>
      ) : isPro ? (
        <div className="px-5 mt-8">
          <div className="bg-white/10 rounded-2xl p-6 border border-white/20 text-center">
            <ShieldCheck size={44} className="text-tp-gold mx-auto mb-3" />
            <p className="font-head text-2xl font-extrabold">Vous êtes Pro 🎉</p>
            {status?.pro_expires_at && <p className="text-white/80 mt-2">Actif jusqu'au <b>{formatDate(status.pro_expires_at)}</b></p>}
          </div>
          <Button data-testid="pro-renew-btn" onClick={() => { setStatus({ ...status, plan: "gratuit" }); }} className="w-full h-12 rounded-full bg-white/15 mt-4 font-semibold">
            Renouveler / prolonger
          </Button>
        </div>
      ) : (
        <div className="px-5 mt-6 space-y-3">
          {/* Plan comparison */}
          <div className="bg-tp-gold rounded-2xl p-5 text-tp-ink">
            <div className="flex items-baseline justify-between mb-3">
              <p className="font-head font-bold text-lg flex items-center gap-2"><Crown size={20} /> Pro</p>
              <p className="font-head font-extrabold text-2xl">{price.toLocaleString("fr-FR")} <span className="text-base font-bold">FCFA/mois</span></p>
            </div>
            <ul className="space-y-2 font-medium">
              <li className="flex items-center gap-2"><InfinityIcon size={18} /> Clients illimités</li>
              <li className="flex items-center gap-2"><ImageIcon size={18} /> Photos illimitées</li>
              <li className="flex items-center gap-2"><Check size={18} /> Sauvegarde cloud prioritaire</li>
            </ul>
          </div>

          {pending ? (
            <div data-testid="pro-pending" className="bg-white/10 border border-white/20 rounded-2xl p-5 text-center">
              <Clock size={36} className="text-tp-gold mx-auto mb-2 tp-pulse" />
              <p className="font-head font-bold text-lg">Paiement en cours de vérification</p>
              <p className="text-white/80 text-sm mt-1">Votre plan Pro sera activé dès confirmation de votre paiement. Merci !</p>
            </div>
          ) : !info?.configured ? (
            <div className="bg-white/10 border border-white/20 rounded-2xl p-5 text-center">
              <p className="text-white/85">Le paiement en ligne n'est pas encore configuré. Réessayez bientôt.</p>
            </div>
          ) : (
            <div className="bg-white rounded-2xl p-5 text-tp-ink">
              <p className="font-head font-bold mb-3">1. Payez {price.toLocaleString("fr-FR")} FCFA</p>
              <div className="grid grid-cols-2 gap-2 mb-3">
                {(info.wave_link || info.wave_number) && (
                  <button data-testid="method-wave" onClick={() => setMethod("wave")}
                    className={`h-12 rounded-xl font-semibold text-sm flex items-center justify-center gap-1.5 border-2 ${method === "wave" ? "border-[#1DC6F2] bg-[#1DC6F2]/10" : "border-gray-200"}`}>
                    <Waves size={18} style={{ color: "#1DC6F2" }} /> Wave
                  </button>
                )}
                {info.om_number && (
                  <button data-testid="method-orange" onClick={() => setMethod("orange")}
                    className={`h-12 rounded-xl font-semibold text-sm flex items-center justify-center gap-1.5 border-2 ${method === "orange" ? "border-[#FF7900] bg-[#FF7900]/10" : "border-gray-200"}`}>
                    <Smartphone size={18} style={{ color: "#FF7900" }} /> Orange Money
                  </button>
                )}
              </div>

              <button
                data-testid="pay-now-btn"
                onClick={payNow}
                className="w-full h-[52px] rounded-xl text-white font-bold flex items-center justify-center gap-2 mb-2"
                style={{ backgroundColor: method === "wave" ? "#1DC6F2" : "#FF7900" }}
              >
                {method === "wave" ? <><Waves size={20} /> Payer avec Wave {info.wave_link && <ExternalLink size={15} />}</> : <><Smartphone size={20} /> Payer avec Orange Money</>}
              </button>

              {method === "orange" && info.om_number && (
                <div className="flex items-center justify-between text-sm text-tp-muted mb-2">
                  <span>Numéro : <b>{info.om_number}</b></span>
                  <button onClick={() => copy(info.om_number, "Numéro")} className="text-tp-green flex items-center gap-1 font-semibold"><Copy size={14} /> Copier</button>
                </div>
              )}
              {method === "wave" && info.wave_number && !info.wave_link && (
                <p className="text-sm text-tp-muted mb-2">Numéro Wave : <b>{info.wave_number}</b></p>
              )}

              <p className="font-head font-bold mt-4 mb-2">2. Confirmez votre paiement</p>
              <Button data-testid="declare-paid-btn" onClick={declarePaid} disabled={submitting}
                className="w-full h-[52px] rounded-full bg-tp-green hover:bg-tp-greenDark font-bold">
                {submitting ? <Loader2 className="animate-spin" size={20} /> : "J'ai payé — activer mon Pro"}
              </Button>
              <p className="text-xs text-tp-muted text-center mt-2">Votre Pro sera activé après vérification du paiement (généralement sous quelques heures).</p>
            </div>
          )}
        </div>
      )}
      <div className="h-10" />
    </div>
  );
}
